// SettingsModal — proper preference-editing surface.
//
// Why this exists:
// Before this component, the ⚙️ button reset the user back into the
// onboarding step machine (setStep(1)), which felt like the app forgot
// who they were and was conversion-killing once a beta tester noticed.
// Settings is fundamentally different from onboarding:
//   - Onboarding = first-time linear setup, ends with "Start planning →"
//   - Settings = incremental edits, can open and close at any time, no
//                step counter, no forced ordering.
//
// What it does:
// Composes the same picker primitives onboarding uses (PeoplePicker,
// DietPicker, AllergyPicker, DislikesPicker, CuisinePicker,
// MealCountPicker, BudgetPicker) inside an accordion-style panel. Saves
// happen on close — the user edits, then taps "Save changes". Cancel
// reverts. There is no step counter and no "Start planning" CTA.
//
// State strategy:
// Local draft state mirrors the parent's preference values on open. We
// commit back to the parent only on save. This means the user can open
// settings, fiddle, change their mind, and tap Cancel without disturbing
// any selected meals or shopping list state.
//
// Mounted in NomLoop.jsx and gated by `open`. The ⚙️ button toggles `open`.

import { useEffect, useRef, useState } from "react";
import { C } from "../theme.js";
import {
  PeoplePicker,
  DietPicker,
  AllergyPicker,
  DislikesPicker,
  CuisinePicker,
  MealCountPicker,
  BudgetPicker,
} from "./preferences/Pickers.jsx";

const SECTIONS = [
  { id: "people",    label: "People",    emoji: "👥" },
  { id: "diet",      label: "Diet",      emoji: "🥗" },
  { id: "allergies", label: "Allergies", emoji: "🚫" },
  { id: "dislikes",  label: "Dislikes",  emoji: "🥦" },
  { id: "cuisines",  label: "Cuisines",  emoji: "🌍" },
  { id: "meals",     label: "Meals/week", emoji: "🍽️" },
  { id: "budget",    label: "Budget",    emoji: "💰" },
];

export function SettingsModal({
  open,
  onClose,
  onLogout,
  // Current values from the parent (NomLoop)
  people,         setPeople,
  diet,           setDiet,
  allergies,      setAllergies,
  dislikes,       setDislikes,
  selectedCuisines, setSelectedCuisines,
  mealTarget,     setMealTarget,
  budget,         setBudget,
  pairHints = {},
}) {
  // Draft state — mirrors the parent's values until the user saves.
  const [draft, setDraft] = useState(null);
  const [activeSection, setActiveSection] = useState("people");
  const dialogRef = useRef(null);

  // Hydrate draft on open. Resetting on every open ensures a fresh edit
  // session — no stale state from a previous open/cancel.
  useEffect(() => {
    if (open) {
      setDraft({
        people,
        diet,
        allergies: [...allergies],
        dislikes: [...dislikes],
        selectedCuisines: [...selectedCuisines],
        mealTarget,
        budget,
      });
      setActiveSection("people");
    }
  }, [open]); // intentionally only re-runs on open transition

  // Esc-to-close + focus management for keyboard accessibility.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    // Move focus into the dialog so screen readers announce it.
    dialogRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !draft) return null;

  const updateDraft = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const save = () => {
    setPeople(draft.people);
    setDiet(draft.diet);
    setAllergies(draft.allergies);
    setDislikes(draft.dislikes);
    setSelectedCuisines(draft.selectedCuisines);
    setMealTarget(draft.mealTarget);
    setBudget(draft.budget);
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-modal-title"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 700,
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.5)", backdropFilter: "blur(4px)" }} />
      <div
        ref={dialogRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative", background: C.white, borderRadius: "24px 24px 0 0",
          width: "100%", maxWidth: 540, padding: "20px 22px 32px",
          boxShadow: "0 -16px 60px rgba(0,0,0,.2)",
          animation: "slideUp .3s ease",
          maxHeight: "92vh", overflowY: "auto",
          outline: "none",
        }}
      >
        {/* Drag handle */}
        <div style={{ width: 36, height: 4, background: C.border, borderRadius: 99, margin: "0 auto 16px" }} />

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <div id="settings-modal-title" style={{ fontFamily: "Lora,serif", fontSize: 22, fontWeight: 700, color: C.charcoal }}>
              Preferences
            </div>
            <div style={{ fontSize: 13, color: C.grey, marginTop: 2 }}>
              Edit your plan, diet, and budget. Changes save when you tap Save.
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close preferences"
            style={{
              width: 36, height: 36, borderRadius: 10, border: `1.5px solid ${C.border}`,
              background: C.white, color: C.charcoal, cursor: "pointer",
              fontSize: 18, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>

        {/* Section nav */}
        <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4, marginBottom: 16 }}>
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              aria-pressed={activeSection === s.id}
              style={{
                flexShrink: 0, padding: "8px 13px", borderRadius: 99,
                border: `1.5px solid ${activeSection === s.id ? C.green : C.border}`,
                background: activeSection === s.id ? C.greenPale : C.white,
                color: activeSection === s.id ? C.green : C.charcoal,
                fontWeight: 700, fontSize: 13, cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              <span style={{ marginRight: 6 }}>{s.emoji}</span>
              {s.label}
            </button>
          ))}
        </div>

        {/* Active section body */}
        <div style={{ marginBottom: 24, minHeight: 200 }}>
          {activeSection === "people" && (
            <SectionTitle title="Who's eating?" subtitle="Portions and shopping scale to this." />
          )}
          {activeSection === "people" && (
            <PeoplePicker value={draft.people} onChange={(v) => updateDraft({ people: v })} />
          )}

          {activeSection === "diet" && (
            <SectionTitle title="Pick your diet" subtitle="We'll filter every meal to match." />
          )}
          {activeSection === "diet" && (
            <DietPicker value={draft.diet} onChange={(v) => updateDraft({ diet: v })} />
          )}

          {activeSection === "allergies" && (
            <SectionTitle title="Allergies to avoid" subtitle="Meals containing these are hidden from your plan." />
          )}
          {activeSection === "allergies" && (
            <AllergyPicker value={draft.allergies} onChange={(v) => updateDraft({ allergies: v })} />
          )}

          {activeSection === "dislikes" && (
            <SectionTitle title="Things you'd rather not eat" subtitle="We'll skip meals featuring these ingredients." />
          )}
          {activeSection === "dislikes" && (
            <DislikesPicker value={draft.dislikes} onChange={(v) => updateDraft({ dislikes: v })} />
          )}

          {activeSection === "cuisines" && (
            <SectionTitle title="Cooking styles" subtitle="Pick up to 2. Two cuisines lets us optimise ingredient overlap." />
          )}
          {activeSection === "cuisines" && (
            <CuisinePicker
              value={draft.selectedCuisines}
              onChange={(v) => updateDraft({ selectedCuisines: v })}
              pairHints={pairHints}
            />
          )}

          {activeSection === "meals" && (
            <SectionTitle title="Meals per week" subtitle="How many dinners are we planning?" />
          )}
          {activeSection === "meals" && (
            <MealCountPicker value={draft.mealTarget} onChange={(v) => updateDraft({ mealTarget: v })} />
          )}

          {activeSection === "budget" && (
            <SectionTitle title="Weekly budget" subtitle="We'll flag when your basket goes over." />
          )}
          {activeSection === "budget" && (
            <BudgetPicker
              value={draft.budget}
              onChange={(v) => updateDraft({ budget: v })}
              people={draft.people}
              mealTarget={draft.mealTarget}
            />
          )}
        </div>

        {/* Persistence reassurance — no account system yet, so we tell the user
            their work is browser-local. Beats silent ambiguity. */}
        <div style={{ marginTop: 8, marginBottom: 8, padding: "10px 14px", borderRadius: 10, background: C.cream, fontSize: 12, color: C.grey, lineHeight: 1.5 }}>
          💾 Your plan is saved in this browser. Use the same browser to pick up where you left off.
        </div>

        {/* Sign out — only shown when a session exists */}
        {onLogout && (
          <button
            onClick={onLogout}
            style={{
              width: "100%", padding: "11px 14px", borderRadius: 10, marginBottom: 8,
              border: `1.5px solid #fde8e8`, background: "#fff5f5",
              color: "#c0392b", fontWeight: 700, fontSize: 13, cursor: "pointer",
              textAlign: "center",
            }}
          >
            Sign out
          </button>
        )}

        {/* Footer actions */}
        <div style={{ display: "flex", gap: 10, position: "sticky", bottom: 0, background: C.white, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: 13, borderRadius: 99,
              border: `1.5px solid ${C.border}`, background: C.white,
              color: C.charcoal, fontWeight: 700, fontSize: 14, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={save}
            style={{
              flex: 2, padding: 13, borderRadius: 99, border: "none",
              background: C.green, color: C.white,
              fontWeight: 800, fontSize: 14, cursor: "pointer",
            }}
          >
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ title, subtitle }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontFamily: "Lora,serif", fontSize: 17, fontWeight: 700, color: C.charcoal, marginBottom: 3 }}>
        {title}
      </div>
      <div style={{ fontSize: 13, color: C.grey, lineHeight: 1.4 }}>{subtitle}</div>
    </div>
  );
}
